import { voucherIdSchema } from '@df/common';
import { z } from 'zod';
import { accountReferenceSchema } from './account';
import { bookYearSchema } from './periods';
import { outcomeIdSchema, queryIdSchema, sampleIdSchema } from './query_metadata';

const internalAccountingDataErrorSchema = z.discriminatedUnion('error', [
  z.object({
    error: z.literal('MissingBookYear'),
    bookYear: bookYearSchema,
  }),
  z.object({
    error: z.literal('TooLongText'),
  }),
  z.object({
    error: z.literal('MissingVoucherId'),
  }),
  z.object({
    error: z.literal('MissingVoucherSeriesText'),
    seriesText: z.string(),
  }),
  z.object({
    error: z.literal('MissingVoucherNumberText'),
    numberText: z.string(),
  }),
  z.object({
    error: z.literal('MissingVoucherText'),
    voucherText: z.string(),
  }),
  z.object({
    error: z.literal('MissingVoucherEntryId'),
  }),
  z.object({
    error: z.literal('MissingAccountReference'),
    account: accountReferenceSchema,
  }),
  z.object({
    error: z.literal('MissingSuperTableValue'),
  }),
  z.object({
    error: z.literal('MissingPeriodRatios'),
  }),
  z.object({
    error: z.literal('MissingBookYearMetadata'),
    bookYear: bookYearSchema,
  }),
  z.object({
    error: z.literal('OutcomeHasMissingBases'),
    outcomeId: outcomeIdSchema,
    query_id: queryIdSchema,
    missing_book_years: bookYearSchema.array(),
  }),
  z.object({
    error: z.literal('XlsxError'),
    message: z.string(),
  }),
  z.object({
    error: z.literal('TinyTemplateError'),
    message: z.string(),
  }),
  z.object({
    error: z.literal('CompanyCacheDeserialization'),
    clientId: z.string(),
  }),
  z.object({
    error: z.literal('YearCacheDeserialization'),
    clientId: z.string(),
    bookYear: bookYearSchema,
  }),
  z.object({
    error: z.literal('UnsupportedExportMimeType'),
    message: z.string(),
  }),
]);

const accountingDataClientErrorSchema = z.discriminatedUnion('error', [
  z.object({
    error: z.literal('CompanyNotInitialized'),
  }),
  z.object({
    error: z.literal('YearNotInitialized'),
  }),
  z.object({
    error: z.literal('YearCacheNotInitialized'),
  }),
  z.object({
    error: z.literal('InvalidQueryId'),
    queryId: queryIdSchema,
  }),
  z.object({
    error: z.literal('InvalidOutcomeId'),
    outcomeId: outcomeIdSchema,
  }),
  z.object({
    error: z.literal('InvalidSampleId'),
    sampleId: sampleIdSchema,
  }),
  z.object({
    error: z.literal('MismatchingClientIds'),
  }),
  z.object({
    error: z.literal('MissingBookYearColumnInSuperTable'),
  }),
  z.object({
    error: z.literal('TriedToSampleNonAmountColumn'),
  }),
  z.object({
    error: z.literal('TriedToExportEmptyColumns'),
  }),
  z.object({
    error: z.literal('InvalidClientId'),
    clientId: z.string(),
  }),
  z.object({
    error: z.literal('InvalidBookYear'),
    bookYear: z.string(),
  }),
  z.object({
    error: z.literal('InvalidDateSpan'),
    span: z.string(),
  }),
  z.object({
    error: z.literal('MissingVoucherId'),
    voucherId: voucherIdSchema,
  }),
  z.object({
    error: z.literal('DayOutOfBounds'),
    day: z.number(),
  }),
  z.object({
    error: z.literal('MonthOutOfBounds'),
    month: z.number(),
  }),
  // This variant is exclusive to the frontend
  z.object({
    error: z.literal('MissingAccount'),
    account: accountReferenceSchema,
  }),
  z.object({
    error: z.literal('InvalidAmountBin'),
    invalidAmountBin: z.number(),
  }),
  z.object({
    error: z.literal('SamplingError'),
    message: z.string(),
  }),
  z.object({
    error: z.literal('InternalError'),
    internalError: internalAccountingDataErrorSchema,
  }),
  z.object({
    error: z.literal('Transport'),
    path: z.string(),
    code: z.number(),
    message: z.string(),
  }),
  z.object({
    error: z.literal('Decode'),
    path: z.string(),
    message: z.string(),
  }),
  z.object({
    error: z.literal('AnnualReportIngestFailed'),
    message: z.string(),
  }),
  // Custom error type, not present in WASM
  z.object({
    error: z.literal('Custom'),
    message: z.string(),
  }),
]);
type AccountingDataClientError = z.infer<typeof accountingDataClientErrorSchema>;

const customError: (message: string) => AccountingDataClientError = (message) => {
  return {
    error: 'Custom',
    message,
  };
};

export { accountingDataClientErrorSchema, customError, type AccountingDataClientError };
